import React, { useState } from 'react';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spacer from '~/components/Spacer/Spacer';
import Checkbox from '~/components/CheckBox/CheckBox';
import { RestrictResidentPayload } from '~/api/resident';

interface IProps {
  showModal: boolean;
  firstName: string;
  isIntegratedBuilding: boolean;
  onClose: () => void;
  onConfirm: (payload: RestrictResidentPayload) => void;
}

// Preview copy shown to the admin. The BACKEND owns the authoritative default
// (correct brand per Building.isHomebodyIntegrated); this is only a prefill so the
// admin can read/edit before sending. We send subject/contents to the API only when
// the admin actually edits them — otherwise the backend fills the correct default.
const previewSubject = 'Your posting access has been paused';

const previewBody = (firstName: string, isIntegrated: boolean) => {
  const brand = isIntegrated ? 'Community powered by Cobu' : 'Cobu';
  // Integrated buildings sign off at "Best regards,"; Cobu buildings add "The Cobu Team".
  const signOff = isIntegrated ? 'Best regards,' : 'Best regards,\nThe Cobu Team';
  return (
    `Hello ${firstName},\n\n` +
    `We've paused your ability to post in ${brand} due to a violation of our Code of Conduct, which you agreed to when you joined.\n\n` +
    `We do this to keep Community a welcoming and respectful space for all neighbors. If you have questions about this decision or would like to request reinstatement, please reach out to support@livecobu.com.\n\n` +
    signOff
  );
};

const BlockUserModal = ({
  showModal,
  firstName,
  isIntegratedBuilding,
  onClose,
  onConfirm
}: IProps) => {
  const [reason, setReason] = useState('');
  const [subject, setSubject] = useState(previewSubject);
  const [contents, setContents] = useState(
    previewBody(firstName, isIntegratedBuilding)
  );
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [contentsEdited, setContentsEdited] = useState(false);
  const [skipEmail, setSkipEmail] = useState(false);

  const handleConfirm = () => {
    onConfirm({
      reason: reason.trim() || undefined,
      // Send only admin-edited values; omitted fields let the backend fill the default.
      subject: subjectEdited ? subject : undefined,
      contents: contentsEdited ? contents : undefined,
      skipEmail
    });
  };

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 480 }}>
      <label className='small-text bold'>Reason (internal, optional)</label>
      <Spacer height={6} />
      <input
        className='default-text'
        value={reason}
        placeholder='Why is this resident being blocked? (optional)'
        onChange={(e) => setReason(e.target.value)}
      />
      <Spacer height={16} />

      <Checkbox
        index={0}
        defaultSetting
        option={{ text: 'Send an email to the resident', value: 'send' }}
        selectedOption={skipEmail ? '' : 'send'}
        setSelectedOption={() => setSkipEmail((prev) => !prev)}
      />

      {!skipEmail && (
        <>
          <Spacer height={16} />
          <label className='small-text bold'>Email subject</label>
          <Spacer height={6} />
          <input
            className='default-text'
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setSubjectEdited(true);
            }}
          />
          <Spacer height={16} />

          <label className='small-text bold'>Email body</label>
          <Spacer height={6} />
          <textarea
            className='default-text'
            rows={6}
            value={contents}
            onChange={(e) => {
              setContents(e.target.value);
              setContentsEdited(true);
            }}
          />
        </>
      )}
    </div>
  );

  return (
    <GenericModal
      showModal={showModal}
      title='Block user'
      content={content}
      actionText='Block user'
      actionFunction={handleConfirm}
      onChange={(open: boolean) => {
        if (!open) {
          onClose();
        }
      }}
    />
  );
};

export default BlockUserModal;
